import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import type { Database } from "@shared/database.types";

type ClientRequest = Database["public"]["Tables"]["client_requests"]["Row"];
type ClientRequestInsert = Database["public"]["Tables"]["client_requests"]["Insert"];
type ClientRequestUpdate = Database["public"]["Tables"]["client_requests"]["Update"];

export function useRequests(opts?: { onlyMine?: boolean }) {
  const { agencyId, user } = useAuth();
  return useQuery({
    queryKey: ["client_requests", agencyId, opts?.onlyMine ?? false],
    enabled: !!agencyId,
    queryFn: async (): Promise<ClientRequest[]> => {
      let q = supabase
        .from("client_requests")
        .select("*")
        .eq("agency_id", agencyId!)
        .order("created_at", { ascending: false });
      if (opts?.onlyMine && user?.id) q = q.eq("user_id", user.id);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as ClientRequest[];
    },
  });
}

export function useCreateRequest() {
  const qc = useQueryClient();
  const { agencyId, user } = useAuth();

  return useMutation({
    mutationFn: async (
      input: Omit<ClientRequestInsert, "agency_id" | "user_id">,
    ): Promise<ClientRequest> => {
      if (!agencyId || !user) throw new Error("Sessione mancante");
      const { data, error } = await supabase
        .from("client_requests")
        .insert({ ...input, agency_id: agencyId, user_id: user.id })
        .select()
        .single();
      if (error) throw error;
      return data as ClientRequest;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["client_requests"] }),
  });
}

export function useUpdateRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: ClientRequestUpdate }) => {
      const { data, error } = await supabase
        .from("client_requests")
        .update(patch)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as ClientRequest;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["client_requests"] }),
  });
}

export function useRealtimeRequests() {
  const qc = useQueryClient();
  const { agencyId } = useAuth();

  useEffect(() => {
    if (!agencyId) return;
    const channelName = `requests:${agencyId}:${Math.random().toString(36).slice(2, 10)}`;
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "client_requests",
          filter: `agency_id=eq.${agencyId}`,
        },
        () => qc.invalidateQueries({ queryKey: ["client_requests"] }),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [agencyId, qc]);
}
